import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { RecurrenceFrequency, SeriesStatus } from '@/lib/recurrence';

// Repeat cleans (recurring_series). Every write goes through SECURITY DEFINER
// RPCs (migration 20261101222418_recurring_visits.sql) so the visits in
// `jobs` always stay in step with the series. The generated Supabase types
// don't include the new tables/RPCs yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface RecurringSeries {
  id: string;
  organization_id: string;
  customer_id: string | null;
  service_id: string | null;
  title: string;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  frequency: RecurrenceFrequency;
  anchor_date: string;
  start_date: string;
  start_time: string;
  duration_minutes: number;
  team_member_ids: string[];
  required_cleaners: number;
  end_date: string | null;
  occurrence_count: number | null;
  status: SeriesStatus;
  notes: string | null;
  generated_until: string | null;
  created_at: string;
}

export interface SeriesClash {
  series_id: string;
  occurrence_date: string;
  clash_detail: string | null;
}

export interface SeriesVisit {
  id: string;
  series_id: string;
  scheduled_date: string;
  start_time: string;
  status: string;
  is_exception: boolean;
}

export interface RecurringSeriesRow extends RecurringSeries {
  nextVisit: SeriesVisit | null;
  upcomingCount: number;
  clashes: SeriesClash[];
  cleanerNames: string[];
}

const SERIES_COLUMNS =
  'id, organization_id, customer_id, service_id, title, client_name, client_email, client_phone, address, frequency, anchor_date, start_date, start_time, duration_minutes, team_member_ids, required_cleaners, end_date, occurrence_count, status, notes, generated_until, created_at';

export const recurringSeriesKey = (orgId: string | null | undefined) => ['recurring-series', orgId] as const;

export function invalidateRecurring(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['recurring-series'] });
  queryClient.invalidateQueries({ queryKey: ['recurring-series-one'] });
  // Visits are jobs: refresh anything job-shaped that uses React Query.
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard-insights'] });
  queryClient.invalidateQueries({ queryKey: ['customers-admin'] });
}

/** Every repeat clean for the org, with next visit, clashes and cleaner names. */
export function useRecurringSeriesList(orgId: string | null | undefined) {
  return useQuery({
    queryKey: recurringSeriesKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<RecurringSeriesRow[]> => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [seriesRes, visitsRes, clashRes, teamRes] = await Promise.all([
        db.from('recurring_series').select(SERIES_COLUMNS).eq('organization_id', orgId).order('created_at', { ascending: false }),
        db
          .from('jobs')
          .select('id, series_id, scheduled_date, start_time, status, is_exception')
          .eq('organization_id', orgId)
          .not('series_id', 'is', null)
          .gte('scheduled_date', today)
          .neq('status', 'cancelled')
          .order('scheduled_date')
          .order('start_time'),
        db
          .from('recurring_series_skips')
          .select('series_id, occurrence_date, clash_detail')
          .eq('organization_id', orgId)
          .eq('reason', 'clash')
          .gte('occurrence_date', today)
          .order('occurrence_date'),
        db.from('team_members').select('id, name').eq('organization_id', orgId),
      ]);
      if (seriesRes.error) throw seriesRes.error;
      if (visitsRes.error) throw visitsRes.error;
      if (clashRes.error) throw clashRes.error;
      if (teamRes.error) throw teamRes.error;

      const names = new Map<string, string>(
        ((teamRes.data ?? []) as { id: string; name: string }[]).map((m) => [m.id, m.name]),
      );
      const visitsBySeries = new Map<string, SeriesVisit[]>();
      for (const v of (visitsRes.data ?? []) as SeriesVisit[]) {
        visitsBySeries.set(v.series_id, [...(visitsBySeries.get(v.series_id) ?? []), v]);
      }
      const clashesBySeries = new Map<string, SeriesClash[]>();
      for (const c of (clashRes.data ?? []) as SeriesClash[]) {
        clashesBySeries.set(c.series_id, [...(clashesBySeries.get(c.series_id) ?? []), c]);
      }

      return ((seriesRes.data ?? []) as RecurringSeries[]).map((s) => {
        const visits = visitsBySeries.get(s.id) ?? [];
        return {
          ...s,
          team_member_ids: s.team_member_ids ?? [],
          nextVisit: visits[0] ?? null,
          upcomingCount: visits.length,
          clashes: s.status === 'active' ? clashesBySeries.get(s.id) ?? [] : [],
          cleanerNames: (s.team_member_ids ?? []).map((id) => names.get(id)).filter((n): n is string => Boolean(n)),
        };
      });
    },
  });
}

/** One series (for the job dialog's repeat panel). */
export function useRecurringSeriesById(seriesId: string | null | undefined) {
  return useQuery({
    queryKey: ['recurring-series-one', seriesId],
    enabled: Boolean(seriesId),
    queryFn: async (): Promise<RecurringSeries | null> => {
      const { data, error } = await db.from('recurring_series').select(SERIES_COLUMNS).eq('id', seriesId).maybeSingle();
      if (error) throw error;
      return (data ?? null) as RecurringSeries | null;
    },
  });
}

const rpc = async <T,>(name: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await db.rpc(name, args);
  if (error) throw error;
  return data as T;
};

export interface CreateSeriesInput {
  jobId: string;
  frequency: RecurrenceFrequency;
  endDate?: string | null;
  occurrenceCount?: number | null;
}

/** Make an existing job the first visit of a new repeat clean. */
export function useCreateSeriesFromJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSeriesInput) => createSeriesFromJob(input),
    onSuccess: () => invalidateRecurring(queryClient),
  });
}

/** Plain async version for flows that already manage their own state. */
export function createSeriesFromJob(input: CreateSeriesInput) {
  return rpc<string>('create_recurring_series_from_job', {
    _job_id: input.jobId,
    _frequency: input.frequency,
    _end_date: input.endDate ?? null,
    _occurrence_count: input.occurrenceCount ?? null,
  });
}

export interface VisitChanges {
  title?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  address?: string;
  service_id?: string;
  scheduled_date?: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
  team_member_ids?: string[];
}

export interface FutureEditResult {
  seriesId: string;
  removed: number;
  created: number;
  kept: number;
}

/** "This and all future visits". */
export function updateSeriesFromVisit(jobId: string, changes: VisitChanges) {
  return rpc<FutureEditResult>('update_recurring_series_from_visit', { _job_id: jobId, _changes: changes });
}

export interface SeriesChanges {
  frequency?: RecurrenceFrequency;
  anchor_date?: string;
  start_time?: string;
  duration_minutes?: number;
  team_member_ids?: string[];
  end_date?: string | null;
  notes?: string;
}

export function useUpdateSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesId, changes }: { seriesId: string; changes: SeriesChanges }) =>
      rpc<{ removed: number; created: number }>('update_recurring_series', { _series_id: seriesId, _changes: changes }),
    onSuccess: () => invalidateRecurring(queryClient),
  });
}

export type SeriesAction = 'pause' | 'resume' | 'end';

export function useSetSeriesStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesId, action, endDate }: { seriesId: string; action: SeriesAction; endDate?: string | null }) =>
      rpc<{ removed: number; created: number }>('set_recurring_series_status', {
        _series_id: seriesId,
        _action: action,
        _end_date: endDate ?? null,
      }),
    onSuccess: () => invalidateRecurring(queryClient),
  });
}

export function skipOccurrence(seriesId: string, occurrenceDate: string) {
  return rpc<null>('skip_recurring_occurrence', { _series_id: seriesId, _occurrence_date: occurrenceDate });
}

export function useSkipOccurrence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesId, occurrenceDate }: { seriesId: string; occurrenceDate: string }) =>
      skipOccurrence(seriesId, occurrenceDate),
    onSuccess: () => invalidateRecurring(queryClient),
  });
}

/** Re-run generation now (e.g. after resolving a clash). */
export function useExtendSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (seriesId: string) => rpc<number>('extend_recurring_series', { _series_id: seriesId, _weeks: 12 }),
    onSuccess: () => invalidateRecurring(queryClient),
  });
}

export interface TeamMemberLite {
  id: string;
  name: string;
}

/** Team members for the repeat-clean cleaner picker. */
export function useRecurringTeamMembers(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['recurring-team-members', orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    queryFn: async (): Promise<TeamMemberLite[]> => {
      const { data, error } = await db.from('team_members').select('id, name').eq('organization_id', orgId).order('name');
      if (error) throw error;
      return (data ?? []) as TeamMemberLite[];
    },
  });
}
